import {
  EntityLinks,
  Goal,
  GoalLifeAreaLinkChange,
  GoalParentLinkChange,
  RelationshipUpdate,
  UpdateGoal,
} from '@griply/types';
import { useCallback } from 'react';

export type GoalLinkChanges = {
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
};

export type MergeGoalLinkChanges = (options: {
  updatedGoals: UpdateGoal[];
  goalParentChanges?: GoalParentLinkChange[];
  goalLifeAreaChanges?: GoalLifeAreaLinkChange[];
  goals: Goal[];
  links?: EntityLinks;
}) => { goalUpdates: UpdateGoal[]; relationshipUpdates: RelationshipUpdate[] };

export type UseMergeGoalLinkChangesOptions = {
  goals: Goal[];
  links?: EntityLinks;
  // Injected: mergeGoalLinkChanges lives in @griply/services, which this package may not import.
  mergeGoalLinkChanges: MergeGoalLinkChanges;
  updateRelationships: (relationshipUpdates: RelationshipUpdate[]) => void;
};

// One dispatch per drop: both link kinds resolve into a single relationship write per goal.
export const useMergeGoalLinkChanges = ({
  goals,
  links,
  mergeGoalLinkChanges,
  updateRelationships,
}: UseMergeGoalLinkChangesOptions) =>
  useCallback(
    (
      updatedGoals: UpdateGoal[],
      { goalParentChanges, goalLifeAreaChanges }: GoalLinkChanges = {},
    ): UpdateGoal[] => {
      const { goalUpdates, relationshipUpdates } = mergeGoalLinkChanges({
        updatedGoals,
        goalParentChanges,
        goalLifeAreaChanges,
        goals,
        links,
      });

      if (relationshipUpdates.length) {
        updateRelationships(relationshipUpdates);
      }

      return goalUpdates;
    },
    [goals, links, mergeGoalLinkChanges, updateRelationships],
  );
